<template>
  <div class="tile is-ancestor is-parent">
    <div class="tile is-parent">
      <article class="tile is-child">
        <div v-if="isLoadingDocuments">
          <div class="is-size-5 mb-2 has-text-grey">Loading documents...</div>
          <div class="columns is-multiline dragable-box">
            <div v-for="n in 8"
              :key="`doc-skeleton-${n}`"
              class="column is-narrow p-2">
              <div class="card document-card skeleton-card">
                <div class="skeleton-block skeleton-check" />
                <div class="card-content document-card-content has-text-centered p-3">
                  <div class="skeleton-block skeleton-icon" />
                  <div class="skeleton-block skeleton-filename" />
                  <div class="skeleton-block skeleton-tag" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="documentsWithInfo.length === 0"
          class="content has-text-grey has-text-centered">
          <span icon="icon is-large">
            <i class="mdi mdi-48px mdi-emoticon-sad" />
          </span>
          <div>No Documents</div>
        </div>
        <template v-else
          v-for="category in categoriesWithDocuments">
          <div :key="category.categoryId"
            class="tile">
            <div class="tile is-parent">
              <article class="tile is-child">
                <div class="columns">
                  <div class="column">
                    <div class="category-name is-size-4 has-text-weight-semibold">{{ category.name }}</div>
                    <div class="is-size-5">
                      {{ subtitle }}
                      <span :class="{ 'has-text-danger': categorySelectedCount(category) > maxSelected }">
                        ({{ categorySelectedCount(category) }}/{{ category.documents.length }})
                      </span>
                    </div>
                  </div>
                  <div class="column is-narrow">
                    <div class="buttons">
                      <a v-if="category.documents.length > 0"
                        class="button tooltip"
                        @click="setSelectedStatusAll(category, true)"
                        data-tooltip="Select all">
                        <span class="icon has-text-success">
                          <i class="mdi mdi-select-all mdi-24px" />
                        </span>
                      </a>
                      <a v-show="categoryHasSelected(category)"
                        class="button tooltip"
                        @click="setSelectedStatusAll(category, false)"
                        data-tooltip="Deselect all">
                        <span class="icon has-text-grey">
                          <i class="mdi mdi-select-off mdi-24px" />
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
                <div class="columns is-multiline dragable-box">
                  <div v-for="(doc, index) in category.documents"
                    :key="doc.quoteAttachmentId || index"
                    class="column is-narrow p-2">
                    <div class="card document-card"
                      :class="{ 'document-card--sent': doc.isSent }"
                      style="box-shadow: 0 0 0 1px #d9d9d9">
                      <header class="card-header">
                        <div class="card-header-title p-2">
                          <div class="control">
                            <div class="pretty p-icon p-curve p-smooth"
                              style="font-size: 0.875rem">
                              <input :id="`chk-pretty-document${category.categoryId}-${index}`"
                                :name="`chk-pretty-select-document${category.categoryId}-${index}`"
                                v-model="doc.isSelected"
                                type="checkbox">
                              <div class="state p-success">
                                <i class="icon mdi mdi-check" />
                                <label />
                              </div>
                            </div>
                          </div>
                        </div>
                      </header>
                      <div class="card-content document-card-content has-text-centered p-2">
                        <figure class="image document-preview">
                          <img class="document-thumb"
                            :src="thumbnailUrl(doc)"
                            alt=""
                            @error="onThumbError">
                        </figure>
                        <p class="document-filename is-size-7"
                          :title="doc.filename">{{ doc.filename }}</p>
                        <div class="document-tags">
                          <span v-if="doc.fileSize"
                            class="tag is-small">{{ formatFileSize(doc.fileSize) }}</span>
                          <span class="tag document-status-tag"
                            :class="doc.isSent ? 'is-success' : 'is-warning'">
                            <span class="icon is-small">
                              <i class="mdi"
                                :class="doc.isSent ? 'mdi-check-circle' : 'mdi-alert-circle-outline'" />
                            </span>
                            <span>{{ doc.isSent ? 'Sent' : 'Not sent' }}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </template>
      </article>
    </div>
  </div>
</template>

<script>
import QuoteService from '@/views/quote/QuoteService'
import EstImageService from '@/services/EstImageService'

const MaximumDocuments = 99
// EstImage document upload accepts PDFs and images only (FQ-2920). Keep in sync
// with AllowedDocumentExtensions on the API (EstImageOnlineService).
const AllowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tif', 'tiff']

export default {
  name: 'DocumentSelect',
  props: {
    value: {
      type: Array,
      default: function () {
        return []
      }
    },
    quoteId: {
      type: String,
      default: ''
    },
    maxSelected: {
      type: Number,
      default: MaximumDocuments
    },
    subtitle: {
      type: String,
      default: 'Select documents to attach'
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    preSelect: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      documentsWithInfo: [],
      categories: [],
      isLoadingDocuments: false
    }
  },
  computed: {
    // Group the flat document list by their category for display.
    categoriesWithDocuments() {
      const groups = {}
      this.documentsWithInfo.forEach((doc) => {
        const key = doc.quoteAttachmentCategoryId || 'uncategorised'
        if (!groups[key]) {
          const category = this.categories.find((c) => c.quoteAttachmentCategoryId === key)
          groups[key] = {
            categoryId: key,
            name: category ? category.name : 'Documents',
            documents: []
          }
        }
        groups[key].documents.push(doc)
      })
      // Within each category, not-yet-sent documents come first (stable sort keeps
      // the server order within each sent/unsent partition).
      const result = Object.values(groups)
      result.forEach((group) => {
        group.documents.sort((a, b) => (a.isSent === b.isSent ? 0 : a.isSent ? 1 : -1))
      })
      return result
    },
    // Watched so the selected list is emitted whenever a checkbox changes.
    selected() {
      return this.documentsWithInfo.map((d) => d.isSelected)
    }
  },
  watch: {
    selected() {
      const selectedDocuments = this.documentsWithInfo
        .filter((d) => d.isSelected)
        .map((d) => ({
          quoteAttachmentId: d.quoteAttachmentId,
          fileName: d.filename,
          fileSize: d.fileSize
        }))
      this.$emit('input', selectedDocuments)
    }
  },
  created() {
    this.loadDocuments()
  },
  methods: {
    // Reuses the existing Documents tab endpoints so the selector shows the quote's
    // real documents, restricted to the PDF/image types EstImage accepts.
    async loadDocuments() {
      this.isLoadingDocuments = true
      try {
        const categories = await QuoteService.getQuoteAttachmentCategories()
        this.categories = Array.isArray(categories) ? categories : []

        const response = await QuoteService.getQuoteAttachments(this.quoteId)
        const data = response && response.data ? response.data : {}

        // Sent-state is best-effort: if it can't be fetched, fall back to showing
        // every document as unsent rather than blanking the selector.
        let sent = []
        try {
          const sentIds = await EstImageService.getSentDocuments(this.quoteId)
          sent = Array.isArray(sentIds) ? sentIds : []
        } catch (e) {
          sent = []
        }

        const documents = []
        // The attachment list is grouped by category server-side: the outer key/row
        // carries the categoryId (the inner attachment summary does not), so read the
        // category from there to group documents the way images are grouped.
        Object.entries(data).forEach(([categoryKey, row]) => {
          const categoryId = (row && row.quoteAttachmentCategoryId) || categoryKey || 'uncategorised'
          const attachments = row && row.quoteAttachments ? row.quoteAttachments : {}
          Object.values(attachments).forEach((attachment) => {
            if (attachment && attachment.deleted) return
            const filename = attachment.filename || attachment.fileName || ''
            // PDF & images only.
            if (!this.isUploadableType(filename)) return
            documents.push({
              quoteAttachmentId: attachment.quoteAttachmentId,
              quoteAttachmentCategoryId: categoryId,
              filename,
              fileSize: attachment.fileSize || attachment.originalFileSize || 0,
              externalStorageUrl: attachment.externalStorageURL || attachment.externalStorageUrl || '',
              isSent: sent.includes(attachment.quoteAttachmentId),
              isSelected: false
            })
          })
        })
        this.documentsWithInfo = documents

        // Mirror the image selector: pre-select documents not yet sent to EstImage.
        // Sent documents still appear (unselected) and remain selectable.
        if (this.preSelect === 'NotSent') {
          this.documentsWithInfo.forEach((d) => { d.isSelected = !d.isSent })
        }
      } catch (e) {
        this.documentsWithInfo = []
      } finally {
        this.isLoadingDocuments = false
      }
    },
    isUploadableType(filename) {
      const extension = (filename || '').split('.').pop().toLowerCase()
      return AllowedExtensions.includes(extension)
    },
    categorySelectedCount(category) {
      return category.documents.reduce((total, doc) => (doc.isSelected ? total + 1 : total), 0)
    },
    categoryHasSelected(category) {
      return category.documents.some((doc) => doc.isSelected)
    },
    setSelectedStatusAll(category, selected) {
      category.documents.forEach((doc) => { doc.isSelected = selected })
    },
    // Mirrors the Documents tab (QuoteAttachment.vue): real image preview for image
    // types, a PDF thumbnail asset for PDFs, a generic doc asset otherwise.
    thumbnailUrl(doc) {
      const filename = (doc.filename || '').toLowerCase()
      const url = doc.externalStorageUrl || ''
      const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tif', '.tiff', '.svg']
      if (filename.endsWith('.pdf')) {
        return require('@/assets/images/attachment-pdf.png')
      }
      if (imageExts.some((ext) => filename.endsWith(ext))) {
        return url || require('@/assets/images/image.png')
      }
      return require('@/assets/images/attachment-doc.png')
    },
    // A signed image URL can expire/404 - fall back to a placeholder image.
    onThumbError(e) {
      e.target.src = require('@/assets/images/image.png')
    },
    formatFileSize(sizeInBytes) {
      if (!sizeInBytes || sizeInBytes === 0) {
        return '0 KB'
      }
      const sizeInKB = Math.round(sizeInBytes / 1024)
      return `${sizeInKB} KB`
    }
  }
}
</script>

<style lang="scss" scoped>
.card-header-title {
  padding: 0.25rem 0.25rem 0.25rem 0.5rem !important;
}

.document-card {
  width: 120px;
  /* Fixed height so every card lines up regardless of filename / tags. */
  height: 218px;
  display: flex;
  flex-direction: column;
  transition: opacity 0.15s ease;
}

/* Already-sent documents are dimmed so the not-yet-sent ones stand out; hovering
   restores full opacity so they stay easy to review/re-select. */
.document-card--sent {
  opacity: 0.5;
}

.document-card--sent:hover {
  opacity: 1;
}

.document-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  overflow: hidden;
}

.document-preview {
  width: 72px;
  height: 72px;
  margin: 0 auto;
}

.document-thumb {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background-color: rgba(128, 128, 128, 0.12);
}

/* Size + Sent/Not sent tags stacked vertically, centred. */
.document-tags {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.document-status-tag {
  white-space: nowrap;
  width: 100%;
  justify-content: center;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.document-filename {
  max-width: 104px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.is-multiline.dragable-box {
  min-height: 3em;
  border: dashed 2px var(--border-color);
  border-radius: 2px;
  margin-left: 2px;
  margin-right: 3px;
  background-color: var(--draggable-box-background-color);
}

/* Skeleton placeholders shown while documents load. */
.skeleton-card {
  width: 120px;
  padding-top: 0.5rem;
}

.skeleton-block {
  border-radius: 4px;
  /* Mid-gray alphas so the shimmer reads on both light and dark themes. */
  background: linear-gradient(90deg, rgba(128, 128, 128, 0.15) 25%, rgba(128, 128, 128, 0.3) 37%, rgba(128, 128, 128, 0.15) 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-check {
  width: 18px;
  height: 18px;
  margin: 0.25rem 0 0 0.5rem;
}

.skeleton-icon {
  width: 48px;
  height: 48px;
  border-radius: 6px;
}

.skeleton-filename {
  width: 88px;
  height: 12px;
}

.skeleton-tag {
  width: 44px;
  height: 12px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
</style>
